// Playing files of encrypted libraries (HRTH-105.5): a reference (library + entry) is
// resolved with the library key into the entry's metadata, the playable blob is chosen (a
// streamable copy if there is one, else the original when the browser plays it), and the
// Service Worker streams it, fetching ciphertext ranges from a signed URL and decrypting
// them here. Used by the Library view, the player bar and parties alike.

import type { LibraryEntry, LibraryRef, PlayResponse } from '../api/types'
import type { MediaKeyInfo } from './attachments'
import { decryptAll } from './attachments'
import { decryptRecord, type OpenCollection } from './collections'
import { fileName, type EntryMeta } from './libraryEntries'
import { registerStream } from './media'
import { rememberBlobs, signedGet } from './sealedStorage'

export interface EntryApi {
  libraryEntries(library: string): Promise<{ entries: LibraryEntry[] }>
  /** `signed`: a GET signed on this device, for a blob in a sealed bucket (the server relays it). */
  blobUrl(blob: string, signed?: string): Promise<{ url: string; expires_at: number }>
}

export interface OpenEntry {
  entry: LibraryEntry
  meta: EntryMeta
}

const CACHE_MS = 30_000
interface Opened {
  files: Map<string, OpenEntry>
  collections: Map<string, OpenCollection>
}
const cache = new Map<string, { at: number; rows: Promise<Opened> }>()

function opened(api: EntryApi, key: string, library: string, fresh: boolean): Promise<Opened> {
  const hit = cache.get(library)
  if (hit && !fresh && Date.now() - hit.at < CACHE_MS) return hit.rows
  const rows = (async () => {
    const out: Opened = { files: new Map(), collections: new Map() }
    for (const entry of (await api.libraryEntries(library)).entries) {
      rememberBlobs(library, entry.blobs.map((b) => b.id))
      const r = await decryptRecord(key, library, entry)
      if (r && 'file' in r) out.files.set(entry.id, { entry, meta: r.file })
      else if (r) out.collections.set(entry.id, { entry, meta: r.collection })
    }
    return out
  })()
  cache.set(library, { at: Date.now(), rows })
  rows.catch(() => cache.delete(library))
  return rows
}

/** Every readable file of a library, decrypted (cached briefly, so a queue of tracks is one fetch). */
export async function openEntries(api: EntryApi, key: string, library: string, fresh = false): Promise<Map<string, OpenEntry>> {
  return (await opened(api, key, library, fresh)).files
}

/** A library's collections (HRTH-164.6), from the same fetch as its files. */
export async function openCollections(api: EntryApi, key: string, library: string, fresh = false): Promise<Map<string, OpenCollection>> {
  return (await opened(api, key, library, fresh)).collections
}

/** Forget cached entries (after an upload, edit or delete). */
export function forgetEntries(library?: string): void {
  if (library) cache.delete(library)
  else cache.clear()
}

/** Where an entry has a lighter copy, which one this device streams. */
export type StreamChoice = 'auto' | 'original' | 'lighter'

const STREAM_CHOICE = 'hrth.streamChoice'

export function streamChoice(): StreamChoice {
  try {
    const v = localStorage.getItem(STREAM_CHOICE)
    return v === 'original' || v === 'lighter' ? v : 'auto'
  } catch {
    return 'auto'
  }
}

export function setStreamChoice(c: StreamChoice): void {
  try {
    localStorage.setItem(STREAM_CHOICE, c)
  } catch {
    // Private windows: the choice lasts this page.
  }
}

/** On Automatic, phones and data savers get the lighter copy, computers the original. */
function wantsLighter(c: StreamChoice): boolean {
  if (c !== 'auto') return c === 'lighter'
  const conn = (navigator as { connection?: { saveData?: boolean } }).connection
  return !!conn?.saveData || (typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches)
}

/**
 * What to play for an entry: its streamable copy, else the original if browsers play it.
 * A lighter copy of an original browsers play is used only when this device wants it.
 */
export function playableBlob(e: OpenEntry, choice: StreamChoice = streamChoice()): { blob: string; info: MediaKeyInfo; mime: string } | null {
  const stream = e.entry.blobs.find((b) => b.role === 'stream')
  const optional = e.meta.copy === 'lighter' && e.meta.streamable && !wantsLighter(choice)
  if (stream && e.meta.blobs.stream && !optional) return { blob: stream.id, info: e.meta.blobs.stream, mime: e.meta.blobs.stream.mime ?? e.meta.mime }
  const original = e.entry.blobs.find((b) => b.role === 'original')
  if (original && e.meta.blobs.original && e.meta.streamable) return { blob: original.id, info: e.meta.blobs.original, mime: e.meta.mime }
  return null
}

/** A reference to an entry, for the player, chat shares and party queues. */
export function entryRef(library: string, e: OpenEntry): LibraryRef {
  const t = e.meta.tags ?? {}
  return {
    library,
    id: e.entry.id,
    kind: e.meta.kind,
    title: t.title ?? fileName(e.meta.path),
    ...(t.artist ? { artist: t.artist } : {}),
    ...(t.duration ? { duration: Math.round(t.duration) } : {}),
    encrypted: true,
  }
}

/** Gives the Service Worker what it needs to stream a library blob; null where there's no worker. */
export function libraryStreamUrl(blob: string, info: MediaKeyInfo, mime: string, token: string): Promise<string | null> {
  const presigned = signedGet(blob)
  return registerStream({ type: 'hrth-library', id: blob, info, mime, token, ...(presigned ? { presigned } : {}) })
}

/** The whole blob, decrypted (images, PDFs, downloads, and playback without a worker). */
export async function decryptBlob(api: EntryApi, blob: string, info: MediaKeyInfo, mime: string): Promise<Blob> {
  const { url } = await api.blobUrl(blob, signedGet(blob))
  const resp = await fetch(url)
  if (!resp.ok) throw new Error(`download failed (${resp.status})`)
  return new Blob([await decryptAll(info, await resp.arrayBuffer())], { type: mime })
}

const images = new Map<string, Promise<string | null>>()

/**
 * One of an entry's images as an object URL (kept for the page's life), or null if it has
 * none: its cover art, a photo's preview, or (for photos without one) the photo itself.
 */
export function entryImage(
  api: EntryApi,
  e: { entry: LibraryEntry; meta: { mime?: string; blobs: Partial<Record<string, MediaKeyInfo & { mime?: string }>> } },
  role: 'art' | 'preview' | 'original' = 'art',
): Promise<string | null> {
  const blob = e.entry.blobs.find((b) => b.role === role)
  const info = e.meta.blobs[role]
  if (!blob || !info) return Promise.resolve(null)
  let hit = images.get(blob.id)
  if (!hit) {
    hit = decryptBlob(api, blob.id, info, info.mime ?? (role === 'original' ? (e.meta.mime ?? 'image/jpeg') : 'image/jpeg'))
      .then((b) => URL.createObjectURL(b))
      .catch(() => {
        images.delete(blob.id)
        return null
      })
    images.set(blob.id, hit)
  }
  return hit
}

/** A photo for a grid: its preview, else the photo. */
export const photoThumb = (api: EntryApi, e: OpenEntry) => (e.meta.blobs.preview ? entryImage(api, e, 'preview') : entryImage(api, e, 'original'))

/** Cover art for a library reference (none for references to folders the server read itself, from before HRTH-183). */
export async function refArt(api: EntryApi, key: string | undefined, ref: LibraryRef): Promise<string | null> {
  if (!ref.encrypted || !key) return null
  const e = (await openEntries(api, key, ref.library)).get(ref.id)
  return e ? entryImage(api, e) : null
}

/** Something a media element can play for an encrypted library reference. */
export async function playEncrypted(api: EntryApi, key: string | undefined, token: string, ref: LibraryRef): Promise<PlayResponse> {
  if (!key) throw new Error('this device doesn’t have the key for that library')
  const e = (await openEntries(api, key, ref.library)).get(ref.id) ?? (await openEntries(api, key, ref.library, true)).get(ref.id)
  if (!e) throw new Error('that file is gone, or you no longer have access to it')
  const p = playableBlob(e)
  if (!p) throw new Error('browsers can’t play this file as it is (it needs a streamable copy)')
  const url = (await libraryStreamUrl(p.blob, p.info, p.mime, token)) ?? URL.createObjectURL(await decryptBlob(api, p.blob, p.info, p.mime))
  return { url, hls: false, mime: p.mime, expires_at: 0 }
}
